<template>
  <v-app>
    <v-snackbar
      v-model="$store.state.snackbar.show"
      bottom
      content-class="py-0 pr-0"
      min-width="fit-content"
      :color="$store.state.snackbar.color"
      :timeout="$store.state.snackbar.timeout"
    >
      <span class="body-1">{{ $store.state.snackbar.text }}</span>
      <v-btn
        class="ml-1"
        text
        small
        elevation="0"
        @click.native="$store.state.snackbar.show = false"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>

    <v-overlay :value="loading">
      <v-chip color="blue" dark>
        {{ $t("global.msg.loading") }}
      </v-chip>
    </v-overlay>

    <application-core v-if="isLoggedIn && !loading" />

    <user-login v-if="!(isLoggedIn || loading || $store.state.keycloak)" />
  </v-app>
</template>

<script>
import ApplicationCore from "@/components/ApplicationCore";
import UserLogin from "@/components/UserLogin.vue";
import Keycloak from "keycloak-js";
import { get, post } from "@/model/api";

export default {
  name: "App",

  components: {
    ApplicationCore,
    UserLogin,
  },

  data() {
    return {
      loading: true,
    };
  },

  computed: {
    isLoggedIn() {
      return this.$store.state.isLoggedIn;
    },
  },

  async created() {
    // Remove the custom scrollbar styles applied by the Prima UI-kit
    // Taken from https://stackoverflow.com/a/66069228
    let styles = document.getElementsByTagName("style");

    for (let i = 0; i < styles?.length; i++) {
      let style = styles[i];
      let rules = style.sheet?.cssRules;

      for (let r = 0; r < rules?.length; r++) {
        let rule = rules[r];

        if (rule.selectorText?.includes("::-webkit-scrollbar")) {
          style.sheet.deleteRule(r);
        }
      }
    }

    const keycloakProps = await get("/authenticate/keycloak-properties").catch(
      (e) => {
        this.$store.commit("showError", e);
      }
    );
    if (!keycloakProps.url) {
      this.loading = false;
      return;
    }
    let authenticated = true;
    let kc = this.$store.state.keycloak;
    if (!kc) {
      kc = new Keycloak(keycloakProps);
      this.$store.commit("setKeycloak", kc);

      const persistTokens = () => {
        sessionStorage.setItem("kc_token", kc.token || "");
        sessionStorage.setItem("kc_refresh_token", kc.refreshToken || "");
        sessionStorage.setItem("kc_id_token", kc.idToken || "");
      };

      kc.onAuthSuccess = () => {
        if (localStorage.attemptingKeycloakLogin === "true") {
          post("/log/keycloak-login").catch((e) => {
            this.$store.commit("showError", e);
          });
          localStorage.removeItem("attemptingKeycloakLogin");
        }
        persistTokens();
      };
      kc.onAuthRefreshSuccess = persistTokens;
      kc.onAuthLogout = () => {
        sessionStorage.removeItem("kc_token");
        sessionStorage.removeItem("kc_refresh_token");
        sessionStorage.removeItem("kc_id_token");
        this.$store.commit("clear");
      };
      kc.onTokenExpired = () => {
        kc.updateToken(30);
      };
      kc.onAuthRefreshError = async () => {
        this.$store.commit("clear");
        localStorage.attemptingKeycloakLogin = "true";
        await kc.login({ redirectUri: window.location.origin });
      };

      const storedToken = sessionStorage.getItem("kc_token");
      const storedRefreshToken = sessionStorage.getItem("kc_refresh_token");
      const storedIdToken = sessionStorage.getItem("kc_id_token");

      authenticated = await kc.init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        checkLoginIframe: false,
        token: storedToken || undefined,
        refreshToken: storedRefreshToken || undefined,
        idToken: storedIdToken || undefined,
      });
    }

    if (!authenticated) {
      this.$store.commit("clear");
      localStorage.attemptingKeycloakLogin = "true";
      await kc.login({ redirectUri: window.location.origin });
      this.loading = false;
      return;
    }

    if (!this.$store.state.isLoggedIn) {
      await get("/authenticate/my-permissions")
        .then((response) => {
          const username = kc.tokenParsed?.preferred_username;
          this.$store.commit("setLoginDetails", {
            response: response,
            username: username,
            i18n: this.$root.$i18n,
          });
        })
        .catch((e) => {
          console.log("Error", e);
          this.$store.commit("showError", e);
          kc.logout({
            redirectUri: window.location.origin,
          });
        });
    }
    this.loading = false;
  },
};
</script>

<style>
.v-btn:focus::before {
  opacity: 0 !important;
}
</style>
